import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { useAuth } from "@/context/AuthContext";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  increment,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { firestore } from "@/firebase/web";
import { useThemeColor } from "@/hooks/useThemeColor";
import { Post } from "@/types/post";
import { formatCount } from "@/utils/formatCount";
import { useFormatTimeAgo } from "@/utils/formatDate";
import { Ionicons } from "@expo/vector-icons";
import React, { useEffect, useState } from "react";
import { AvatarImage } from "@/components/ui/AvatarImage";
import { AuthorName } from "@/components/ui/AuthorName";
import AlertModal from "@/components/ui/AlertModal.web";
import { useTranslation } from "react-i18next";
import { router } from "expo-router";
import {
  ActivityIndicator,
  FlatList,
  Keyboard,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";

type Comment = {
  id: string;
  text: string;
  createdAt: Timestamp;
  author: {
    uid: string;
    displayName?: string;
    username?: string;
    photoURL?: string;
    isVerified?: boolean;
  };
  likes?: number;
};

const CommentItem = ({
  item,
  postId,
  setComments,
}: {
  item: Comment;
  postId: string;
  setComments: React.Dispatch<React.SetStateAction<Comment[]>>;
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(item.likes || 0);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [errorAlert, setErrorAlert] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const iconColor = useThemeColor({}, "icon");
  const { formatTimeAgo } = useFormatTimeAgo();
  const timeAgo = formatTimeAgo(
    item.createdAt?.toDate?.()?.toISOString() ?? ""
  );

  useEffect(() => {
    if (!user?.uid) return;
    const likeRef = doc(
      firestore,
      "posts",
      postId,
      "comments",
      item.id,
      "likes",
      user.uid
    );
    const unsubscribe = onSnapshot(likeRef, (doc) => {
      setLiked(doc.exists());
    });
    return () => unsubscribe();
  }, [item.id, user?.uid, postId]);

  const handleLike = async () => {
    if (!user?.uid) {
      router.push("/(auth)/login");
      return;
    }

    const commentRef = doc(firestore, "posts", postId, "comments", item.id);
    const likeRef = doc(commentRef, "likes", user.uid);

    const newLiked = !liked;
    setLiked(newLiked);
    setLikesCount((count) => count + (newLiked ? 1 : -1));

    try {
      if (newLiked) {
        await setDoc(likeRef, { createdAt: new Date() });
        await updateDoc(commentRef, { likes: increment(1) });
      } else {
        await deleteDoc(likeRef);
        await updateDoc(commentRef, { likes: increment(-1) });
      }
    } catch (error) {
      console.error("Error updating like status:", error);
      setLiked(!newLiked);
      setLikesCount((count) => count - (newLiked ? 1 : -1));
    }
  };

  const handleLongPress = () => {
    if (user?.uid !== item.author.uid) {
      return;
    }
    setDeleteConfirmVisible(true);
  };

  const confirmDelete = () => {
    setDeleteConfirmVisible(false);
    const commentRef = doc(firestore, "posts", postId, "comments", item.id);
    deleteDoc(commentRef)
      .then(() => {
        setComments((prevComments) =>
          prevComments.filter((comment) => comment.id !== item.id)
        );
      })
      .catch((e) => {
        console.error("Failed to delete comment", e);
        setErrorAlert({
          title: t("ERROR"),
          message: t("COULD_NOT_DELETE_COMMENT"),
        });
      });
  };

  return (
    <>
      <AlertModal
        visible={deleteConfirmVisible}
        title={t("DELETE_COMMENT")}
        message={t("ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_COMMENT")}
        onClose={() => setDeleteConfirmVisible(false)}
        primaryAction={{
          text: t("DELETE"),
          onPress: confirmDelete,
          destructive: true,
        }}
        secondaryAction={{
          text: t("CANCEL"),
          onPress: () => setDeleteConfirmVisible(false),
        }}
      />
      <AlertModal
        visible={!!errorAlert}
        title={errorAlert?.title ?? ""}
        message={errorAlert?.message ?? ""}
        onClose={() => setErrorAlert(null)}
      />
      <TouchableOpacity onLongPress={handleLongPress}>
        <ThemedView style={styles.commentContainer}>
        <ThemedView
          style={{
            flex: 1,
            flexDirection: "row",
            alignItems: "flex-start",
            maxWidth: "100%",
          }}
        >
          <ThemedView>
            <AvatarImage
              photoURL={item.author.photoURL}
              alt={item.author.displayName ?? ""}
              style={styles.avatar}
            />
          </ThemedView>
          <ThemedView style={{ flex: 1, marginRight: 10 }}>
            <ThemedView
              style={{
                flexDirection: "row",
                alignItems: "center",
                minWidth: 0,
              }}
            >
              <AuthorName
                displayName={item.author.displayName}
                username={item.author.username}
                isVerified={item.author.isVerified}
              />
              {timeAgo ? (
                <ThemedText
                  style={{ color: "#666", marginLeft: 6, flexShrink: 0 }}
                >
                  · {timeAgo}
                </ThemedText>
              ) : null}
            </ThemedView>
            <ThemedView
              style={{ flexDirection: "row", alignItems: "flex-start" }}
            >
              <ThemedText style={{ flex: 1 }}>{item.text}</ThemedText>
              <ThemedView style={styles.likeContainer}>
                <TouchableOpacity onPress={handleLike}>
                  <Ionicons
                    name={liked ? "heart" : "heart-outline"}
                    size={22}
                    color={liked ? "#e74c3c" : iconColor}
                  />
                </TouchableOpacity>
                <ThemedText>{formatCount(likesCount)}</ThemedText>
              </ThemedView>
            </ThemedView>
          </ThemedView>
        </ThemedView>
      </ThemedView>
      </TouchableOpacity>
    </>
  );
};

function CommentsInner({
  postId,
  ListHeaderComponent,
}: {
  postId: Post["id"];
  ListHeaderComponent?: React.ReactElement;
}) {
  const { user, userData } = useAuth();
  const { t } = useTranslation();
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [postErrorVisible, setPostErrorVisible] = useState(false);
  const tintColor = useThemeColor({}, "tint");

  useEffect(() => {
    if (!postId) return;

    const commentsCollection = collection(
      firestore,
      "posts",
      postId,
      "comments"
    );
    const q = query(commentsCollection, orderBy("createdAt", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const commentsData: Comment[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          commentsData.push({
            id: doc.id,
            text: data.text,
            createdAt: data.createdAt,
            author: data.author,
            likes: data.likes || 0,
          });
        });
        setComments(commentsData);
        setLoading(false);
      },
      (err) => {
        // Race: the author deletes the parent post and the rules on this
        // subcollection start denying reads before we unsubscribe. The screen
        // is already navigating away — silence the noise; log anything else.
        if (
          (err as { code?: string } | undefined)?.code === "permission-denied"
        ) {
          return;
        }
        console.error("Comments listener error", err);
      },
    );

    return () => {
      unsubscribe();
      setComments([]);
      setLoading(true);
    };
  }, [postId]);

  const handleAddComment = async () => {
    if (!user) {
      router.push("/(auth)/login");
      return;
    }
    if (!postId || !newComment.trim()) return;

    const commentsCollection = collection(
      firestore,
      "posts",
      postId,
      "comments"
    );
    try {
      await addDoc(commentsCollection, {
        text: newComment,
        author: {
          uid: user.uid,
          displayName: userData?.displayName ?? user.displayName,
          photoURL: userData?.photoURL ?? user.photoURL,
          ...(userData?.username ? { username: userData.username } : {}),
          ...(userData?.isVerified ? { isVerified: userData.isVerified } : {}),
        },
        createdAt: serverTimestamp(),
      });
      // Only clear the input after the write succeeds — otherwise a failed
      // post (offline, rules) silently loses what the user typed.
      setNewComment("");
      Keyboard.dismiss();
    } catch (e) {
      console.error("Failed to add comment", e);
      setPostErrorVisible(true);
    }
  };

  if (loading) {
    return (
      <ThemedView style={styles.container}>
        <ActivityIndicator size="large" color={tintColor} />
      </ThemedView>
    );
  }

  if (!user) {
    return (
      <View style={styles.container}>
        <FlatList
          data={[]}
          renderItem={() => null}
          ListHeaderComponent={ListHeaderComponent}
          ListEmptyComponent={
            <ThemedView style={{ padding: 40, alignItems: "center", gap: 16 }}>
              <ThemedText style={{ textAlign: "center", fontSize: 16 }}>
                {t("PLEASE_LOGIN_TO_SEE_COMMENTS")}
              </ThemedText>
              <TouchableOpacity
                onPress={() => router.push("/(auth)/login")}
                style={{
                  backgroundColor: tintColor,
                  paddingHorizontal: 24,
                  paddingVertical: 12,
                  borderRadius: 24,
                }}
              >
                <ThemedText style={{ color: "white", fontWeight: "bold" }}>
                  {t("LOGIN")}
                </ThemedText>
              </TouchableOpacity>
            </ThemedView>
          }
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ThemedView style={styles.commentsContainer}>
        <FlatList
          data={comments}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <CommentItem
              item={item}
              postId={postId!}
              setComments={setComments}
            />
          )}
          ListHeaderComponent={ListHeaderComponent}
          ListEmptyComponent={
            <ThemedText
              style={{
                textAlign: "center",
                marginTop: 20,
              }}
            >
              {t("NO_COMMENTS_YET")}
            </ThemedText>
          }
        />
      </ThemedView>
      <ThemedView style={styles.inputContainer}>
        <TextInput
          testID="comment-input"
          style={styles.input}
          value={newComment}
          onChangeText={setNewComment}
          placeholder={t("ADD_A_COMMENT")}
        />
        <TouchableOpacity
          testID="comment-send-button"
          onPress={handleAddComment}
        >
          <Ionicons name="send" size={24} color={tintColor} />
        </TouchableOpacity>
      </ThemedView>
      <AlertModal
        visible={postErrorVisible}
        title={t("ERROR")}
        message={t("COULD_NOT_POST_COMMENT")}
        onClose={() => setPostErrorVisible(false)}
      />
    </View>
  );
}

export const Comments = ({
  postId,
  ListHeaderComponent,
}: // keyboardVerticalOffset is ignored on web
{
  postId: Post["id"];
  ListHeaderComponent?: React.ReactElement;
  keyboardVerticalOffset?: number;
}) => {
  return (
    <CommentsInner postId={postId} ListHeaderComponent={ListHeaderComponent} />
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  commentsContainer: {
    flex: 1,
    justifyContent: "center",
  },
  commentContainer: {
    padding: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  commentAuthor: {
    fontWeight: "bold",
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 12,
  },
  inputContainer: {
    flexDirection: "row",
    alignItems: "center",
    padding: 10,
    borderTopWidth: 1,
    borderTopColor: "#ccc",
  },
  input: {
    flex: 1,
    height: 40,
    borderColor: "gray",
    borderWidth: 1,
    borderRadius: 20,
    paddingHorizontal: 15,
    marginRight: 10,
  },
  likeContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
});
